import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, inr } from "../lib/api";
import { PageHead, Badge, Progress } from "../components/ui-bits";
import { metaFor, SERVICE_LIST, downloadBlob } from "../lib/services";
import { toast } from "sonner";
import {
  User, CalendarBlank, CheckCircle, XCircle, UploadSimple, FileText, Clock, Plus,
  DownloadSimple, File, ArrowRight, SealCheck
} from "@phosphor-icons/react";

const statusKey = (s) => (s === "Completed" ? "completed" : (s || "").includes("Waiting") ? "overdue" : "upcoming");

function ProjectCard({ p, i, onDecide, onUpload, uploadingId }) {
  const fileRefs = useRef({});
  return (
    <div data-testid={`project-${i}`} className="card p-5">
      <div className="flex justify-between items-start mb-3 gap-3">
        <div>
          <h3 className="font-head font-bold">{p.service}</h3>
          <p className="text-sm text-[#555] flex items-center gap-3 mt-1 flex-wrap">
            <span className="flex items-center gap-1"><User size={14} /> {p.expert}</span>
            <span className="flex items-center gap-1"><CalendarBlank size={14} /> {p.due || "—"}</span>
            <span className="flex items-center gap-1"><Clock size={14} /> {p.stage}</span>
          </p>
        </div>
        <Badge status={statusKey(p.status)}>{p.status}</Badge>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <Progress value={p.progress} />
        <span className="tnum text-sm font-semibold w-12 text-right">{p.progress}%</span>
      </div>
      {p.remarks && <p className="text-xs text-[#555] mb-3">Remarks: {p.remarks}</p>}
      <div className="bg-black/[0.02] rounded-sm p-4 border border-black/5">
        <p className="label mb-2 flex items-center gap-1"><FileText size={12} /> Documents requested by your agent</p>
        {(p.required_docs || []).length === 0 ? (
          <p className="text-xs text-[#888]">No documents needed right now.</p>
        ) : (
          <div className="space-y-2">
            {p.required_docs.map((r, ri) => (
              <div key={r.id} data-testid={`req-doc-${i}-${ri}`} className="flex items-center gap-3 text-sm">
                <span className={`w-2 h-2 rounded-full ${r.status === "received" ? "bg-[#00A86B]" : "bg-[#F5A623]"}`} />
                <span className="flex-1">{r.name}</span>
                {r.status === "received" ? (
                  <span className="text-xs text-[#00A86B] flex items-center gap-1"><CheckCircle size={14} weight="fill" /> {r.uploaded_name}</span>
                ) : (
                  <>
                    <input ref={(el) => (fileRefs.current[r.id] = el)} type="file" hidden data-testid={`req-file-${i}-${ri}`}
                      onChange={(e) => e.target.files[0] && onUpload(p, r, e.target.files[0])} />
                    <button data-testid={`req-upload-${i}-${ri}`} disabled={uploadingId === r.id} onClick={() => fileRefs.current[r.id]?.click()}
                      className="text-xs bg-[#002FA7] hover:bg-[#00227A] text-white px-3 py-1 rounded-sm inline-flex items-center gap-1 transition-colors disabled:opacity-50">
                      <UploadSimple size={13} /> {uploadingId === r.id ? "Uploading…" : "Upload"}
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {p.approval_status === "pending" && (
        <div data-testid={`approval-${i}`} className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-[#8a6d00]">Your agent marked this complete — approve to close it.</p>
          <div className="flex gap-2">
            <button data-testid={`approve-${i}`} onClick={() => onDecide(p, "approved")}
              className="text-sm bg-[#00A86B] hover:brightness-95 text-white px-3 py-1.5 rounded-sm flex items-center gap-1.5 transition-all"><CheckCircle size={16} /> Approve</button>
            <button data-testid={`reject-${i}`} onClick={() => onDecide(p, "rejected")}
              className="text-sm border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/5 px-3 py-1.5 rounded-sm flex items-center gap-1.5 transition-colors"><XCircle size={16} /> Request changes</button>
          </div>
        </div>
      )}
      {p.approval_status === "approved" && <p className="mt-2 text-xs text-[#00A86B] font-semibold">Approved by you</p>}
    </div>
  );
}

function ServiceRecords({ docs }) {
  const dl = async (d) => {
    try { const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" }); downloadBlob(r.data, d.name); }
    catch { toast.error("Could not download file"); }
  };
  return (
    <div className="card p-5" data-testid="service-records">
      <p className="label mb-3 flex items-center gap-1"><File size={12} /> Records & Documents ({docs.length})</p>
      {docs.length === 0 ? <p className="text-xs text-[#888]">No records for this service yet.</p> : (
        <div className="divide-y divide-black/10">
          {docs.map((d, i) => (
            <div key={d.id} data-testid={`svc-doc-${i}`} className="flex items-center gap-3 py-2 text-sm">
              {d.source === "agent" ? <SealCheck size={18} className="text-[#00A86B]" weight="fill" /> : <File size={18} className="text-[#002FA7]" />}
              <span className="flex-1 truncate">{d.name}</span>
              <span className="text-xs text-[#888] hidden sm:inline">{d.source === "agent" ? "Deliverable" : "Your upload"} · {(d.uploaded_at || "").slice(0, 10)}</span>
              <button data-testid={`svc-doc-dl-${i}`} onClick={() => dl(d)} className="text-[#002FA7] hover:bg-[#002FA7]/10 p-1 rounded-sm"><DownloadSimple size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function JobRequests({ reqs, onDecide }) {
  if (!reqs.length) return null;
  return (
    <div data-testid="my-requests" className="mb-6">
      <p className="label mb-2">My Additional Requests</p>
      <div className="card divide-y divide-black/10">
        {reqs.map((r, i) => (
          <div key={r.id} data-testid={`jobreq-row-${i}`} className="p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-medium">{r.title} <span className="text-xs bg-black/5 px-2 py-0.5 rounded-sm">{r.category}</span></p>
              {r.status === "quoted" && <p className="text-xs text-[#002FA7] mt-0.5">Quote: <b className="tnum">{inr(r.quote_amount)}</b>{r.quote_note ? ` · ${r.quote_note}` : ""}</p>}
            </div>
            {r.status === "quoted" ? (
              <div className="flex gap-2">
                <button data-testid={`jr-accept-${i}`} onClick={() => onDecide(r, "accepted")} className="text-sm bg-[#00A86B] hover:brightness-95 text-white px-3 py-1.5 rounded-sm transition-all">Accept</button>
                <button data-testid={`jr-decline-${i}`} onClick={() => onDecide(r, "declined")} className="text-sm border border-black/20 hover:bg-black/5 px-3 py-1.5 rounded-sm transition-colors">Decline</button>
              </div>
            ) : <Badge status={r.status === "accepted" ? "completed" : r.status === "requested" ? "upcoming" : "overdue"}>{r.status === "requested" ? "Awaiting quote" : r.status}</Badge>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Projects() {
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [subscribed, setSubscribed] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploadingId, setUploadingId] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [showReq, setShowReq] = useState(false);
  const [jr, setJr] = useState({ title: "", category: "GST", description: "" });

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  const loadReqs = () => api.get("/job-requests").then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadReqs();
    api.get("/dashboard/services").then((r) => setSubscribed(r.data.services.map((s) => s.service))).catch(() => {});
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  }, []);

  const services = useMemo(() => {
    const cats = projects.map((p) => p.category || "Other");
    return [...new Set([...subscribed, ...cats])];
  }, [projects, subscribed]);
  const active = params.get("service") || services[0];
  const items = projects.filter((p) => (p.category || "Other") === active);
  const svcDocs = docs.filter((d) => (d.service || d.folder) === active);

  const decide = async (p, decision) => {
    await api.patch(`/projects/${p.id}/approval`, { decision });
    toast.success(decision === "approved" ? "Task approved & closed" : "Sent back to your agent");
    load();
  };
  const uploadReqDoc = async (p, req, file) => {
    setUploadingId(req.id);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/projects/${p.id}/required-docs/${req.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded "${file.name}"`); load();
      api.get("/documents").then((r) => setDocs(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingId(null); }
  };
  const submitReq = async (e) => {
    e.preventDefault();
    if (!jr.title.trim()) { toast.error("Describe the job you need"); return; }
    await api.post("/job-requests", jr);
    toast.success("Request sent — your admin will send a quote soon");
    setJr({ title: "", category: "GST", description: "" }); setShowReq(false); loadReqs();
  };
  const decideReq = async (r, decision) => {
    await api.post(`/job-requests/${r.id}/decision`, { decision });
    toast.success(decision === "accepted" ? "Accepted — job added to your tracker" : "Quote declined");
    loadReqs(); if (decision === "accepted") load();
  };

  const meta = metaFor(active);
  const avg = items.length ? Math.round(items.reduce((a, p) => a + (p.progress || 0), 0) / items.length) : 0;

  return (
    <div>
      <PageHead eyebrow="Work Monitoring · By Service" title="Project Tracker">
        <button data-testid="request-job-btn" onClick={() => setShowReq(!showReq)}
          className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors">
          <Plus size={16} /> Request a Job
        </button>
      </PageHead>

      {showReq && (
        <form onSubmit={submitReq} data-testid="request-job-form" className="card p-6 mb-6 grid sm:grid-cols-4 gap-3 items-end fade-up">
          <div><label className="label block mb-1">Service</label>
            <select data-testid="jr-category" value={jr.category} onChange={(e) => setJr({ ...jr, category: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {[...SERVICE_LIST, "Other"].map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <div className="sm:col-span-2"><label className="label block mb-1">What do you need?</label>
            <input data-testid="jr-title" value={jr.title} onChange={(e) => setJr({ ...jr, title: e.target.value })} placeholder="e.g. One-time GST annual return (GSTR-9)" className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
          <button data-testid="jr-submit" className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm transition-colors">Send Request</button>
          <div className="sm:col-span-4"><textarea data-testid="jr-desc" rows={2} value={jr.description} onChange={(e) => setJr({ ...jr, description: e.target.value })} placeholder="Any details…" className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
        </form>
      )}

      <JobRequests reqs={reqs} onDecide={decideReq} />

      {services.length === 0 ? (
        <p className="card p-8 text-sm text-[#888] text-center">No services yet. Your CFO team will set these up shortly.</p>
      ) : (
        <>
          <p className="label mb-3">Your Services — select one to open its work</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="service-buttons">
            {services.map((s) => {
              const m = metaFor(s);
              const on = s === active;
              const sp = projects.filter((p) => (p.category || "Other") === s);
              const pend = sp.reduce((a, p) => a + (p.required_docs || []).filter((r) => r.status !== "received").length, 0);
              return (
                <button key={s} data-testid={`svc-btn-${s.toLowerCase()}`} onClick={() => setParams({ service: s })}
                  className={`relative text-left p-4 rounded-sm border-2 transition-[transform,background-color,border-color] hover:-translate-y-0.5 ${on ? "text-white" : "bg-white hover:bg-black/[0.02]"}`}
                  style={{ borderColor: m.color, background: on ? m.color : undefined }}>
                  <m.Icon size={24} weight="fill" style={{ color: on ? "#fff" : m.color }} />
                  <p className="font-head font-bold mt-2">{s}</p>
                  <p className={`text-xs ${on ? "text-white/80" : "text-[#888]"}`}>{sp.length} job(s)</p>
                  {pend > 0 && <span className="absolute top-2 right-2 text-[10px] font-bold bg-[#FF3B30] text-white rounded-full px-1.5 py-0.5">{pend}</span>}
                </button>
              );
            })}
          </div>

          <div data-testid="service-panel" className="fade-up">
            <div className="card p-5 mb-4 flex flex-wrap items-center gap-4" style={{ borderLeft: `4px solid ${meta.color}` }}>
              <meta.Icon size={28} weight="fill" style={{ color: meta.color }} />
              <div className="min-w-0">
                <p className="font-head font-black text-xl">{active}</p>
                <p className="text-xs text-[#888]">{meta.desc} · {items.length} job(s) · {svcDocs.length} record(s)</p>
              </div>
              <div className="ml-auto flex items-center gap-3">
                <div className="w-32"><Progress value={avg} /></div>
                <span className="tnum text-sm font-semibold">{avg}%</span>
                {active === "Bookkeeping" && (
                  <Link to="/bookkeeping" data-testid="open-bookkeeping" className="bg-[#0F766E] hover:brightness-95 text-white text-sm px-3 py-2 rounded-sm inline-flex items-center gap-1">
                    Open Bookkeeping <ArrowRight size={14} /></Link>
                )}
              </div>
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-4">
                {items.map((p, i) => <ProjectCard key={p.id} p={p} i={i} onDecide={decide} onUpload={uploadReqDoc} uploadingId={uploadingId} />)}
                {items.length === 0 && <p className="card p-6 text-sm text-[#888]">No jobs started for {active} yet.</p>}
              </div>
              <ServiceRecords docs={svcDocs} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
